import React, { useState } from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import * as authApi from '../authApi';
import { isValidEmail } from '../util/email';

interface Props { navigate: (s: Screen) => void; user: User | null; }

export default function Contact({ navigate, user }: Props) {
  const home: Screen = user ? 'dashboard' : 'landing';
  const [name, setName] = useState(user ? `${user.firstName} ${user.lastName}`.trim() : '');
  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState(''); // honeypot — real people leave it blank
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr('');
    if (!isValidEmail(email)) { setErr('Please enter a valid email so we can reply.'); return; }
    if (!message.trim()) { setErr('Please enter a message.'); return; }
    setBusy(true);
    try {
      await authApi.sendContact({ name: name.trim(), email: email.trim(), message: message.trim(), ...(company ? { company } : {}) } as any);
      setSent(true);
    } catch (e: any) {
      setErr(e?.message || "We couldn't send your message just now. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate(home)} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Contact Us</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 32 }}>
        {sent ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>✅</p>
            <h2 style={{ marginBottom: 8 }}>Message sent</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
              Thanks for reaching out. We received your message and will reply to <strong>{email.trim()}</strong>.
            </p>
            <button onClick={() => navigate(home)} className="btn-primary">Done</button>
          </div>
        ) : (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              Questions, feedback, or need help with your account or a school license? Send us a note and we'll get back to you by email.
            </p>
            <div className="card">
              <form onSubmit={submit}>
                <div className="form-group">
                  <label>Your name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="First and last name" />
                </div>
                <div className="form-group">
                  <label>Your email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@school.org" />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="How can we help?" rows={6} style={{ resize: 'vertical' }} />
                </div>
                {/* Honeypot: hidden from people, tempting to bots. */}
                <input type="text" value={company} onChange={e => setCompany(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
                {err && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 12 }}>{err}</div>}
                <button type="submit" className="btn-primary" disabled={busy || !email || !message.trim()}>{busy ? 'Sending…' : 'Send message'}</button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
